import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatBadgeModule } from '@angular/material/badge';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { WalletService } from '../../../core/services/wallet.service';
import { AuthService } from '../../../core/auth/auth.service';
import { MerchantService } from '../../../core/auth/merchant.service';
import { ThemeService } from '../../../core/services/theme.service';
import { MerchantContextService } from '../../../core/services/merchant-context.service';
import { NotificationService } from '../../../core/services/notification.service';
import { NotificationDto } from '../../../core/models/api.models';
import { BusinessAccountDto } from '../../../core/models/api.models';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule, RouterLink,
    MatIconModule, MatButtonModule, MatMenuModule,
    MatBadgeModule, MatDividerModule, MatTooltipModule
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent {
  wallet = inject(WalletService);
  auth = inject(AuthService);
  merchants = inject(MerchantService);
  theme = inject(ThemeService);
  context = inject(MerchantContextService);
  private router = inject(Router);
  notifications = inject(NotificationService);
  businessMenuOpen = signal(false);

  /** Caps the badge so a long-ignored feed doesn't render "247" over a 40px button. */
  unreadBadge = computed(() => {
    const n = this.notifications.unreadCount();
    return n > 99 ? '99+' : String(n);
  });

  /** Marking read is optimistic, so navigation doesn't wait on the round-trip. */
  openNotification(item: NotificationDto): void {
    this.notifications.markRead(item);
    if (item.actionUrl) this.router.navigateByUrl(item.actionUrl);
  }

  get accountType() { return this.context.accountType(); }
  get btcPrice() { return this.wallet.btcPrice(); }
  get selectedBusiness() { return this.context.selectedBusiness(); }
  get businessAccounts() { return this.context.businesses(); }
  get hasBusinesses() { return this.context.hasBusinesses(); }
  get loadingBusinesses() { return this.context.loading(); }
  get firstName() { return this.auth.firstName(); }
  get displayName() { return this.auth.displayName(); }
  get initials() { return this.auth.initials(); }

  /** Email shown in the user menu — from the merchant account, falling back to the token claims. */
  get email(): string {
    const m = this.merchants.merchant();
    if (m?.email) return m.email;
    const acc = this.auth.account();
    if (!acc) return '';
    const claims = (acc.idTokenClaims ?? {}) as Record<string, unknown>;
    return (claims['email'] as string | undefined) ?? acc.username ?? '';
  }

  /** Hamburger: collapse the sidebar on desktop, open the drawer on mobile. */
  toggleNav(): void {
    if (window.innerWidth <= 768) this.wallet.toggleMobileSidebar();
    else this.wallet.toggleSidebar();
  }

  setAccountType(type: 'Individual' | 'Business') {
    this.context.setAccountType(type);
    // Switching away from Business hides its sections; don't leave the user on a page the
    // navigation no longer has.
    const section = this.router.url.split('?')[0].split('/')[1] ?? '';
    if (!this.context.showsSection(section)) this.router.navigate(['/dashboard']);
  }

  selectBusiness(b: BusinessAccountDto) {
    this.context.selectBusiness(b);
  }

  /** No business accounts yet — send the merchant straight to the create form. */
  addBusiness() {
    this.router.navigate(['/business/entities/new']);
  }

  logout(): void {
    this.auth.logout();
  }
}
